/*! @azure/msal-browser-1p v3.23.0 2024-09-03 */
'use strict';
import { PerformanceEvents } from './TelemetryUtils.mjs';
import { MATSTelemetryReporter } from './MATSTelemetryReporter.mjs';
import { PerformanceTelemetryReporter } from './PerformanceTelemetryReporter.mjs';

/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
const allowedEvents = new Set([
    PerformanceEvents.AcquireTokenSilent,
    PerformanceEvents.AcquireTokenByCode,
    PerformanceEvents.AcquireTokenPopup,
    PerformanceEvents.AcquireTokenRedirect,
    PerformanceEvents.AcquireTokenPreRedirect,
    PerformanceEvents.InitializeClientApplication,
    PerformanceEvents.SsoSilent,
    // Embedded events
    PerformanceEvents.EmbeddedClientApplicationSendSSOSilentRequest,
    PerformanceEvents.EmbeddedClientApplicationSendPopupRequest,
    PerformanceEvents.EmbeddedClientApplicationSendRedirectRequest,
    PerformanceEvents.EmbeddedClientApplicationHandleRedirectRequest,
    PerformanceEvents.EmbeddedClientApplicationSendSilentRefreshRequest,
]);
class TelemetryReporter {
    constructor(logger, configuration) {
        this.logger = logger;
        const eventsToEmit = configuration?.eventsToEmit?.filter((event) => allowedEvents?.has(event)) || [...allowedEvents];
        this.matsTelemetryReporter = configuration?.enableMATS
            ? new MATSTelemetryReporter(logger, eventsToEmit, configuration?.appName, configuration?.appVersion)
            : null;
        this.perfTelemetryReporter = new PerformanceTelemetryReporter(logger, eventsToEmit, configuration?.synthetic);
    }
    sendCustomEvent(event) {
        this.perfTelemetryReporter.sendCustomEvent(event);
        this.matsTelemetryReporter?.sendCustomEvent(event);
    }
    getTelemetryCallback() {
        this.logger.trace("TelemetryReporter: Attaching callback to send custom metrics.");
        const telemetryCallback = (events) => {
            events.forEach((event) => {
                /*
                 * Set proper event name for broker event such that instead of grouping all broker events under the same table
                 * they are placed in the corresponding tables based on the underlying API names
                 * e.g. acquireTokenByBroker -> (acquireTokenSilent | ssoSilent | acquireTokenRedirect | acquireTokenPopup)
                 */
                if (event.name === PerformanceEvents.AcquireTokenByBroker) {
                    event.name = event.brokerEventName || event.name;
                    event.isBroker = true;
                    delete event.brokerEventName;
                    this.logger.verbosePii(`TelemetryReporter: Updated broker event ${JSON.stringify(event)}`, event.correlationId);
                }
                this.sendCustomEvent(event);
            });
        };
        this.logger.verbose("TelemetryReporter: Returning callback as telemetry emitted.");
        return telemetryCallback;
    }
}

export { TelemetryReporter };
//# sourceMappingURL=TelemetryReporter.mjs.map
